/**
 * @fileoverview Match3 Header Component
 * @description Timer, score, and combo display for the game
 */

import React, { useEffect, useRef } from "react";
import { View, Text, StyleSheet, Animated, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";

interface Match3HeaderProps {
  score: number;
  timeRemaining: number;
  comboLevel: number;
  isPaused: boolean;
  onPausePress: () => void;
  timeBonusActive?: boolean;
  bonusSeconds?: number;
}

/**
 * Header component showing game stats
 */
export const Match3Header: React.FC<Match3HeaderProps> = ({
  score,
  timeRemaining,
  comboLevel,
  isPaused,
  onPausePress,
  timeBonusActive = false,
  bonusSeconds = 0,
}) => {
  const colors = useThemeColors();
  const scoreAnim = useRef(new Animated.Value(1)).current;
  const comboAnim = useRef(new Animated.Value(1)).current;
  const bonusAnim = useRef(new Animated.Value(0)).current;
  const prevScore = useRef(score);

  // Animate score changes
  useEffect(() => {
    if (score !== prevScore.current) {
      prevScore.current = score;
      Animated.sequence([
        Animated.timing(scoreAnim, {
          toValue: 1.2,
          duration: 100,
          useNativeDriver: true,
        }),
        Animated.timing(scoreAnim, {
          toValue: 1,
          duration: 100,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [score, scoreAnim]);

  // Animate combo changes
  useEffect(() => {
    if (comboLevel > 1) {
      Animated.sequence([
        Animated.timing(comboAnim, {
          toValue: 1.3,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(comboAnim, {
          toValue: 1,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [comboLevel, comboAnim]);

  // Animate time bonus
  useEffect(() => {
    if (timeBonusActive) {
      bonusAnim.setValue(1);
      Animated.timing(bonusAnim, {
        toValue: 0,
        duration: 2000,
        useNativeDriver: true,
      }).start();
    }
  }, [timeBonusActive, bonusAnim]);

  // Format time as mm:ss
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Timer color based on remaining time
  const getTimerColor = (): string => {
    if (timeRemaining <= 10) return colors.error;
    if (timeRemaining <= 30) return colors.warning;
    return colors.success;
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.surface }]}>
      {/* Score */}
      <View style={styles.statBox}>
        <Text style={[styles.label, { color: colors.textSecondary }]}>Score</Text>
        <Animated.Text
          style={[
            styles.value,
            { color: colors.primary, transform: [{ scale: scoreAnim }] },
          ]}
        >
          {score.toLocaleString()}
        </Animated.Text>
      </View>

      {/* Timer */}
      <View style={styles.timerBox}>
        <View style={styles.timerContent}>
          <Ionicons name="time-outline" size={20} color={getTimerColor()} />
          <Text style={[styles.timerValue, { color: getTimerColor() }]}>
            {formatTime(timeRemaining)}
          </Text>
        </View>
        
        {/* Time bonus indicator */}
        <Animated.View
          style={[
            styles.bonusIndicator,
            {
              backgroundColor: colors.success,
              opacity: bonusAnim,
              transform: [{ 
                translateY: bonusAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [-20, 0],
                })
              }],
            },
          ]}
        >
          <Text style={styles.bonusText}>+{bonusSeconds}s</Text>
        </Animated.View>
      </View>

      {/* Combo / Pause */}
      <View style={styles.rightSection}>
        {comboLevel > 1 && (
          <Animated.View
            style={[
              styles.comboBox,
              {
                backgroundColor: colors.warningLight,
                transform: [{ scale: comboAnim }],
              },
            ]}
          >
            <Text style={[styles.comboText, { color: colors.warning }]}>
              {comboLevel}x Combo!
            </Text>
          </Animated.View>
        )}
        
        <TouchableOpacity
          onPress={onPausePress}
          style={[styles.pauseButton, { backgroundColor: colors.surfaceSecondary }]}
          accessibilityLabel={isPaused ? "Resume game" : "Pause game"}
          accessibilityRole="button"
        >
          <Ionicons
            name={isPaused ? "play" : "pause"}
            size={20}
            color={colors.text}
          />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
    marginHorizontal: 16,
    marginBottom: 16,
  },
  statBox: {
    alignItems: "center",
    minWidth: 80,
  },
  label: {
    fontSize: 12,
    fontWeight: "500",
    marginBottom: 2,
  },
  value: {
    fontSize: 24,
    fontWeight: "bold",
  },
  timerBox: {
    alignItems: "center",
    position: "relative",
  },
  timerContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  timerValue: {
    fontSize: 28,
    fontWeight: "bold",
    fontVariant: ["tabular-nums"],
  },
  bonusIndicator: {
    position: "absolute",
    top: -24,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  bonusText: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
  rightSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    minWidth: 80,
    justifyContent: "flex-end",
  },
  comboBox: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  comboText: {
    fontSize: 12,
    fontWeight: "bold",
  },
  pauseButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default Match3Header;

